import { useState } from 'react';
import { useQuery, useLazyQuery, useMutation } from '@apollo/client/react';
import { useParams, useNavigate } from 'react-router-dom';
import { gql } from '@apollo/client';
import { Navigation } from '../components/Navigation';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { DocumentUpload } from '../components/DocumentUpload';
import { DocumentCard } from '../components/DocumentCard';
import { FileViewerModal, FileItem } from '../components/FileViewerModal';
import { TaskDetailModal } from '../components/TaskDetailModal';
import { AppointmentModal } from '../components/calendar/AppointmentModal';
import { VideoConferencesList } from '../components/video/VideoConferencesList';
import { ScheduleConferenceModal } from '../components/video/ScheduleConferenceModal';
import { LIST_VIDEO_CONFERENCES_BY_CASE } from '../graphql/queries';
import { 
  LIST_TASKS_BY_CASE, 
  CREATE_TASK
} from '../graphql/tasks';

const GET_CASE = gql`
  query GetCase($id: ID!) {
    getCase(id: $id) {
      id
      caseNumber
      status
      caseType
      description
      incidentDate
      clientId
      assignedAttorneyId
      assignedCaseManagerId
      createdAt
      updatedAt
    }
  }
`;

const GET_CURRENT_USER = gql`
  query GetCurrentUser {
    getCurrentUser {
      id
      email
      firstName
      lastName
      role
    }
  }
`;

const LIST_DOCUMENTS_BY_CASE = gql`
  query ListDocumentsByCase($caseId: ID!) {
    listDocumentsByCase(caseId: $caseId) {
      id
      fileName
      fileType
      fileSize
      description
      createdAt
      uploadedBy
      uploader {
        id
        firstName
        lastName
      }
    }
  }
`;

const LIST_TASK_TEMPLATES = gql`
  query ListTaskTemplates {
    listTaskTemplates {
      id
      name
      title
      description
      taskType
      priority
      defaultDueDays
      category
    }
  }
`;

const GET_DOCUMENT_DOWNLOAD_URL = gql`
  query GetDocumentDownloadUrl($id: ID!) {
    getDocumentDownloadUrl(id: $id)
  }
`;

const LIST_STAFF = gql`
  query ListStaff {
    listStaff {
      id
      firstName
      lastName
      role
      email
    }
  }
`;

const UPDATE_CASE_STATUS = gql`
  mutation UpdateCaseStatus($id: ID!, $status: CaseStatus!) {
    updateCaseStatus(id: $id, status: $status) {
      id
      status
      updatedAt
    }
  }
`;

const ASSIGN_CASE = gql`
  mutation AssignCase($id: ID!, $attorneyId: ID, $caseManagerId: ID) {
    assignCase(id: $id, attorneyId: $attorneyId, caseManagerId: $caseManagerId) {
      id
      assignedAttorneyId
      assignedCaseManagerId
      updatedAt
    }
  }
`;

// Type definitions for Apollo queries/mutations
interface Case {
  id: string;
  caseNumber: string;
  status: string;
  caseType: string;
  description?: string;
  incidentDate?: string;
  clientId: string;
  assignedAttorneyId?: string;
  assignedCaseManagerId?: string;
  createdAt: string;
  updatedAt: string;
}

interface GetCaseData {
  getCase: Case;
}

interface CurrentUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
}

interface GetCurrentUserData {
  getCurrentUser: CurrentUser;
}

interface Document {
  id: string;
  fileName: string;
  fileType: string;
  fileSize: number;
  description?: string;
  createdAt: string;
  uploadedBy?: string;
  uploader?: {
    id: string;
    firstName: string;
    lastName: string;
  };
}

interface ListDocumentsByCaseData {
  listDocumentsByCase: Document[];
}

interface TaskTemplate {
  id: string;
  name: string;
  title: string;
  description?: string;
  taskType: string;
  priority: string;
  defaultDueDays?: number;
  category?: string;
}

interface ListTaskTemplatesData {
  listTaskTemplates: TaskTemplate[];
}

interface Task {
  id: string;
  title: string;
  description?: string;
  taskType: string;
  priority: string;
  status: string;
  dueDate: string;
  [key: string]: any;
}

interface ListTasksByCaseData {
  listTasksByCase: {
    items: Task[];
    nextToken?: string;
  };
}

interface StaffMember {
  id: string;
  firstName: string;
  lastName: string;
  role: string;
  email?: string;
}

interface ListStaffData {
  listStaff: StaffMember[];
}

interface VideoConference {
  id: string;
  title: string;
  status: string;
  scheduledStartTime: string;
  [key: string]: any;
}

interface ListVideoConferencesByCaseData {
  listVideoConferencesByCase: VideoConference[];
}

interface GetDocumentDownloadUrlData {
  getDocumentDownloadUrl: string;
}

export function CaseDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'overview' | 'documents' | 'tasks' | 'timeline'>('overview');
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showCreateTaskModal, setShowCreateTaskModal] = useState(false);
  const [showTaskDetailModal, setShowTaskDetailModal] = useState(false);
  const [showAppointmentModal, setShowAppointmentModal] = useState(false);
  const [showVideoConferenceModal, setShowVideoConferenceModal] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<string>('');
  const [selectedAttorney, setSelectedAttorney] = useState<string>('');
  const [selectedCaseManager, setSelectedCaseManager] = useState<string>('');
  const [selectedTask, setSelectedTask] = useState<any>(null);
  
  // File viewer state
  const [fileViewerVisible, setFileViewerVisible] = useState(false);
  const [fileViewerFiles, setFileViewerFiles] = useState<FileItem[]>([]);
  const [fileViewerIndex, setFileViewerIndex] = useState(0);
  
  // Task form state
  const [taskTitle, setTaskTitle] = useState('');
  const [taskDescription, setTaskDescription] = useState('');
  const [taskType, setTaskType] = useState('DOCUMENT_UPLOAD');
  const [taskPriority, setTaskPriority] = useState('MEDIUM');
  const [taskDueDate, setTaskDueDate] = useState('');
  const [taskStaffNotes, setTaskStaffNotes] = useState('');
  const [selectedTemplateId, setSelectedTemplateId] = useState('');

  const { data, loading, error, refetch } = useQuery<GetCaseData>(GET_CASE, {
    variables: { id },
    skip: !id,
  });

  const caseData = data?.getCase;

  const { data: userData } = useQuery<GetCurrentUserData>(GET_CURRENT_USER);
  const currentUser = userData?.getCurrentUser;

  const { data: documentsData, refetch: refetchDocuments } = useQuery<ListDocumentsByCaseData>(LIST_DOCUMENTS_BY_CASE, {
    variables: { caseId: id },
    skip: !id,
  });

  const { data: templatesData } = useQuery<ListTaskTemplatesData>(LIST_TASK_TEMPLATES);
  const templates = templatesData?.listTaskTemplates || [];

  const { data: tasksData, refetch: refetchTasks } = useQuery<ListTasksByCaseData>(LIST_TASKS_BY_CASE, {
    variables: { caseId: id },
    skip: !id,
    fetchPolicy: 'network-only',
  });

  const { data: staffData } = useQuery<ListStaffData>(LIST_STAFF);

  // Fetch video conferences for this case
  const { data: videoConferencesData, loading: videoConferencesLoading, refetch: refetchVideoConferences } = useQuery<ListVideoConferencesByCaseData>(LIST_VIDEO_CONFERENCES_BY_CASE, {
    variables: { caseId: id },
    skip: !id,
  });

  const [getDownloadUrl] = useLazyQuery<GetDocumentDownloadUrlData>(GET_DOCUMENT_DOWNLOAD_URL);
  const [updateCaseStatus] = useMutation(UPDATE_CASE_STATUS);
  const [assignCase] = useMutation(ASSIGN_CASE);
  const [createTask] = useMutation(CREATE_TASK);

  const documents = documentsData?.listDocumentsByCase || [];
  const tasks = tasksData?.listTasksByCase?.items || [];
  const staffMembers = staffData?.listStaff || [];
  const attorneys = staffMembers.filter((s: any) => s.role === 'ATTORNEY');
  const caseManagers = staffMembers.filter((s: any) => s.role === 'CASE_MANAGER' || s.role === 'LEAD_CASE_MANAGER');
  const videoConferences = videoConferencesData?.listVideoConferencesByCase || [];

  const assignedAttorney = staffMembers.find((s: any) => s.id === caseData?.assignedAttorneyId);
  const assignedCaseManager = staffMembers.find((s: any) => s.id === caseData?.assignedCaseManagerId);

  const handleDownload = async (documentId: string) => {
    try {
      const { data } = await getDownloadUrl({
        variables: { id: documentId },
      });

      if (data?.getDocumentDownloadUrl) {
        // Open the pre-signed URL in a new tab to download
        window.open(data.getDocumentDownloadUrl, '_blank');
      }
    } catch (error: any) {
      console.error('Error downloading document:', error);
      alert('Error downloading document: ' + error.message);
    }
  };

  const handlePreviewDocument = async (doc: any, index: number) => {
    try {
      // Get download URLs for all documents to enable navigation
      const filesPromises = documents.map(async (d: any) => {
        const { data } = await getDownloadUrl({ variables: { id: d.id } });
        return {
          url: data?.getDocumentDownloadUrl || '',
          fileName: d.fileName,
          fileType: d.fileType,
        };
      });
      
      const files = await Promise.all(filesPromises);
      setFileViewerFiles(files.filter(f => f.url));
      setFileViewerIndex(index);
      setFileViewerVisible(true);
    } catch (error: any) {
      console.error('Error loading document preview:', error);
      // Fallback to download
      handleDownload(doc.id);
    }
  };

  const handleStatusUpdate = async () => {
    if (!selectedStatus) return;
    
    try {
      await updateCaseStatus({
        variables: {
          id,
          status: selectedStatus,
        },
      });
      await refetch();
      setShowStatusModal(false);
      setSelectedStatus('');
    } catch (error: any) {
      console.error('Error updating case status:', error);
      alert('Error updating case status: ' + error.message);
    }
  };

  const handleAssignment = async () => {
    try {
      await assignCase({
        variables: {
          id,
          attorneyId: selectedAttorney || undefined,
          caseManagerId: selectedCaseManager || undefined,
        },
      });
      await refetch();
      setShowAssignModal(false);
      setSelectedAttorney('');
      setSelectedCaseManager('');
    } catch (error: any) {
      console.error('Error assigning case:', error);
      alert('Error assigning case: ' + error.message);
    }
  };

  const handleCreateTask = async () => {
    if (!taskTitle.trim() || !taskDescription.trim() || !taskDueDate) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      await createTask({
        variables: {
          input: {
            caseId: id,
            clientId: caseData!.clientId,
            title: taskTitle,
            description: taskDescription,
            taskType,
            priority: taskPriority,
            dueDate: taskDueDate,
            staffNotes: taskStaffNotes || undefined,
          },
        },
      });
      await refetchTasks();
      setShowCreateTaskModal(false);
      // Reset form
      setTaskTitle('');
      setTaskDescription('');
      setTaskType('DOCUMENT_UPLOAD');
      setTaskPriority('MEDIUM');
      setTaskDueDate('');
      setTaskStaffNotes('');
      alert('Task assigned successfully!');
    } catch (error: any) {
      console.error('Error creating task:', error);
      alert('Error creating task: ' + error.message);
    }
  };

  const getStatusBadgeVariant = (status: string): 'default' | 'secondary' | 'success' | 'warning' | 'info' => {
    const variants: Record<string, any> = {
      NEW: 'info',
      IN_PROGRESS: 'warning',
      NEGOTIATION: 'warning',
      LITIGATION: 'warning',
      SETTLED: 'success',
      CLOSED: 'default',
    };
    return variants[status] || 'default';
  };

  const getTaskStatusBadgeVariant = (status: string): 'default' | 'secondary' | 'success' | 'warning' | 'info' => {
    const variants: Record<string, any> = {
      PENDING: 'warning',
      IN_PROGRESS: 'info',
      COMPLETED: 'success',
      VERIFIED: 'success',
      REJECTED: 'default',
      OVERDUE: 'default',
    };
    return variants[status] || 'default';
  };

  const getTaskPriorityBadgeVariant = (priority: string): 'default' | 'secondary' | 'success' | 'warning' | 'info' => {
    const variants: Record<string, any> = {
      LOW: 'secondary',
      MEDIUM: 'info',
      HIGH: 'warning',
      URGENT: 'default',
    };
    return variants[priority] || 'default';
  };

  const getTaskTypeIcon = (type: string) => {
    const baseClasses = "w-5 h-5";
    const icons: Record<string, JSX.Element> = {
      DOCUMENT_UPLOAD: (
        <svg className={baseClasses} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
        </svg>
      ),
      APPOINTMENT: (
        <svg className={baseClasses} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      ),
      FORM_COMPLETION: (
        <svg className={baseClasses} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      ),
      COMMUNICATION: (
        <svg className={baseClasses} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
        </svg>
      ),
      REVIEW: (
        <svg className={baseClasses} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
        </svg>
      ),
      COMPLIANCE: (
        <svg className={baseClasses} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      OTHER: (
        <svg className={baseClasses} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
        </svg>
      ),
    };
    return icons[type] || icons.OTHER;
  };

  const isTaskOverdue = (dueDate: string, status: string) => {
    if (status === 'COMPLETED' || status === 'VERIFIED') return false;
    return new Date(dueDate) < new Date();
  };

  const getDaysUntilDue = (dueDate: string) => {
    const today = new Date();
    const due = new Date(dueDate);
    const diffTime = due.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return `Overdue by ${Math.abs(diffDays)} days`;
    if (diffDays === 0) return 'Due today';
    if (diffDays === 1) return 'Due tomorrow';
    return `Due in ${diffDays} days`;
  };

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatDateTime = (dateString: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-aalaw-red"></div>
      </div>
    );
  }

  if (error || !caseData) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <h2 className="text-xl font-bold text-red-600">Error loading case</h2>
          <p className="text-gray-600 mt-2">{error?.message || 'Case not found'}</p>
          <Button onClick={() => navigate('/dashboard')} className="mt-4">
            Back to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation user={currentUser} />
      
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button variant="ghost" onClick={() => navigate(-1)}>
                ← Back
              </Button>
              <div>
                <h1 className="text-xl font-bold text-aalaw-charcoal font-mono">
                  {caseData.caseNumber}
                </h1>
                <p className="text-sm text-gray-600">{caseData.caseType}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Badge variant={getStatusBadgeVariant(caseData.status)}>
                {caseData.status}
              </Badge>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Tabs */}
            <Card>
              <CardContent className="p-0">
                <div className="flex border-b border-gray-200">
                  <button
                    onClick={() => setActiveTab('overview')}
                    className={`flex-1 px-6 py-4 text-sm font-semibold transition-colors ${
                      activeTab === 'overview'
                        ? 'text-aalaw-red border-b-2 border-aalaw-red'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    Overview
                  </button>
                  <button
                    onClick={() => setActiveTab('documents')}
                    className={`flex-1 px-6 py-4 text-sm font-semibold transition-colors ${
                      activeTab === 'documents'
                        ? 'text-aalaw-red border-b-2 border-aalaw-red'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    Documents ({documents.length})
                  </button>
                  <button
                    onClick={() => setActiveTab('tasks')}
                    className={`flex-1 px-6 py-4 text-sm font-semibold transition-colors ${
                      activeTab === 'tasks'
                        ? 'text-aalaw-red border-b-2 border-aalaw-red'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    Tasks ({tasks.length})
                  </button>
                  <button
                    onClick={() => setActiveTab('timeline')}
                    className={`flex-1 px-6 py-4 text-sm font-semibold transition-colors ${
                      activeTab === 'timeline'
                        ? 'text-aalaw-red border-b-2 border-aalaw-red'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    Timeline
                  </button>
                </div>

                <div className="p-6">
                  {/* Overview Tab */}
                  {activeTab === 'overview' && (
                    <div className="space-y-6">
                      {/* Status Workflow */}
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Case Status Workflow</h3>
                        <div className="relative">
                          {/* Status Timeline */}
                          <div className="flex items-center justify-between mb-4">
                            {['NEW', 'IN_PROGRESS', 'NEGOTIATION', 'LITIGATION', 'SETTLED', 'CLOSED'].map((status, index, arr) => {
                              const isActive = status === caseData.status;
                              const isPast = arr.indexOf(caseData.status) > index;
                              
                              return (
                                <div key={status} className="flex-1 flex items-center">
                                  <div className="flex flex-col items-center flex-1">
                                    <div
                                      className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold text-sm transition-all ${
                                        isActive
                                          ? 'bg-aalaw-red text-white ring-4 ring-red-100'
                                          : isPast
                                          ? 'bg-green-500 text-white'
                                          : 'bg-gray-200 text-gray-500'
                                      }`}
                                    >
                                      {isPast ? '✓' : index + 1}
                                    </div>
                                    <span className={`text-xs mt-2 text-center ${isActive ? 'font-semibold text-gray-900' : 'text-gray-600'}`}>
                                      {status.replace('_', ' ')}
                                    </span>
                                  </div>
                                  {index < arr.length - 1 && (
                                    <div
                                      className={`h-1 flex-1 mx-2 ${
                                        isPast ? 'bg-green-500' : 'bg-gray-200'
                                      }`}
                                    />
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>

                      {/* Basic Information */}
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h3>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="text-sm font-medium text-gray-600">Case Number</label>
                            <p className="text-sm text-gray-900 mt-1 font-mono">{caseData.caseNumber}</p>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-600">Status</label>
                            <p className="text-sm text-gray-900 mt-1">{caseData.status}</p>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-600">Case Type</label>
                            <p className="text-sm text-gray-900 mt-1">{caseData.caseType}</p>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-600">Incident Date</label>
                            <p className="text-sm text-gray-900 mt-1">{formatDate(caseData.incidentDate)}</p>
                          </div>
                          <div className="col-span-2">
                            <label className="text-sm font-medium text-gray-600">Description</label>
                            <p className="text-sm text-gray-900 mt-1">{caseData.description}</p>
                          </div>
                        </div>
                      </div>

                      {/* Timeline */}
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Timeline</h3>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="text-sm font-medium text-gray-600">Created</label>
                            <p className="text-sm text-gray-900 mt-1">{formatDate(caseData.createdAt)}</p>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-600">Last Updated</label>
                            <p className="text-sm text-gray-900 mt-1">{formatDate(caseData.updatedAt)}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Timeline Tab */}
                  {activeTab === 'timeline' && (
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Status History</h3>
                      <div className="space-y-4">
                        {/* Current Status */}
                        <div className="flex gap-4">
                          <div className="flex flex-col items-center">
                            <div className="w-3 h-3 rounded-full bg-aalaw-red"></div>
                            <div className="w-0.5 h-full bg-gray-200 mt-2"></div>
                          </div>
                          <div className="flex-1 pb-8">
                            <div className="flex items-center gap-2 mb-1">
                              <Badge variant={getStatusBadgeVariant(caseData.status)}>
                                {caseData.status}
                              </Badge>
                              <span className="text-xs text-gray-500">Current Status</span>
                            </div>
                            <p className="text-sm text-gray-600">
                              Last updated: {formatDateTime(caseData.updatedAt)}
                            </p>
                          </div>
                        </div>

                        {/* Case Created */}
                        <div className="flex gap-4">
                          <div className="flex flex-col items-center">
                            <div className="w-3 h-3 rounded-full bg-green-500"></div>
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-sm font-semibold text-gray-900">Case Created</span>
                            </div>
                            <p className="text-sm text-gray-600">
                              {formatDateTime(caseData.createdAt)}
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                        <p className="text-sm text-blue-900">
                          <strong>Note:</strong> Full status history tracking will be implemented in a future update.
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Documents Tab */}
                  {activeTab === 'documents' && (
                    <div className="space-y-4">
                      {/* Upload Section */}
                      <DocumentUpload
                        entityId={id!}
                        entityType="case"
                        onUploadComplete={async () => {
                          console.log('Document upload complete, refetching documents...');
                          await refetchDocuments();
                        }}
                      />

                      {/* Existing Documents */}
                      <div className="mt-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Case Documents</h3>
                        {documents.length === 0 ? (
                          <p className="text-center text-gray-500 py-8">No documents uploaded yet</p>
                        ) : (
                          <div className="space-y-3">
                            {documents.map((doc: any, index: number) => (
                              <DocumentCard
                                key={doc.id}
                                document={doc}
                                getDownloadUrl={getDownloadUrl}
                                onPreview={() => handlePreviewDocument(doc, index)}
                                onDownload={() => handleDownload(doc.id)}
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Tasks Tab */}
                  {activeTab === 'tasks' && (
                    <div className="space-y-4">
                      {/* Header with Create Button */}
                      <div className="flex items-center justify-between">
                        <h3 className="text-lg font-semibold text-gray-900">Client Tasks</h3>
                        <Button 
                          onClick={() => setShowCreateTaskModal(true)}
                        >
                          Assign New Task
                        </Button>
                      </div>

                      {/* Task List */}
                      {tasks.length === 0 ? (
                        <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                          <svg className="w-12 h-12 mx-auto mb-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                          </svg>
                          <p className="text-gray-600 mb-4">No tasks assigned yet</p>
                          <Button 
                            variant="outline"
                            onClick={() => setShowCreateTaskModal(true)}
                          >
                            Assign First Task
                          </Button>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {tasks.map((task: any) => {
                            const overdue = isTaskOverdue(task.dueDate, task.status);
                            return (
                              <div
                                key={task.id}
                                onClick={() => {
                                  setSelectedTask(task);
                                  setShowTaskDetailModal(true);
                                }}
                                className={`p-4 border rounded-lg cursor-pointer transition-all hover:shadow-md ${
                                  overdue && task.status !== 'COMPLETED' && task.status !== 'VERIFIED'
                                    ? 'border-red-300 bg-red-50'
                                    : task.status === 'VERIFIED'
                                    ? 'border-green-300 bg-green-50'
                                    : 'border-gray-200 hover:bg-gray-50'
                                }`}
                              >
                                <div className="flex items-start gap-4">
                                  <div className="text-gray-600 mt-1">{getTaskTypeIcon(task.taskType)}</div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-start justify-between gap-2 mb-2">
                                      <h4 className="text-sm font-semibold text-gray-900">{task.title}</h4>
                                      <div className="flex gap-2 flex-shrink-0">
                                        <Badge variant={getTaskPriorityBadgeVariant(task.priority)}>
                                          {task.priority}
                                        </Badge>
                                        <Badge variant={getTaskStatusBadgeVariant(task.status)}>
                                          {task.status}
                                        </Badge>
                                      </div>
                                    </div>
                                    <p className="text-xs text-gray-600 mb-2 line-clamp-2">{task.description}</p>
                                    <div className="flex items-center gap-4 text-xs text-gray-500">
                                      <span className={overdue && task.status !== 'COMPLETED' && task.status !== 'VERIFIED' ? 'text-red-600 font-semibold' : ''}>
                                        {getDaysUntilDue(task.dueDate)}
                                      </span>
                                      <span>•</span>
                                      <span>Assigned by {task.assignedByName || 'Staff'}</span>
                                      {task.completedAt && (
                                        <>
                                          <span>•</span>
                                          <span className="text-green-600">✓ Completed {formatDate(task.completedAt)}</span>
                                        </>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Success Message */}
            <Card className="bg-green-50 border-green-200">
              <CardContent className="pt-6">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0">
                    <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-green-900">Case Created Successfully</h3>
                    <p className="text-sm text-green-800 mt-1">
                      This case has been created from an inquiry. A client account has been set up and the client will receive login credentials.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Actions */}
          <div className="space-y-6">
            {/* Quick Actions */}
            <Card>
              <CardHeader>
                <CardTitle>Quick Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Button 
                  variant="outline" 
                  className="w-full justify-start text-gray-900"
                  onClick={() => setShowStatusModal(true)}
                >
                  <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Update Status
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full justify-start text-gray-900"
                  onClick={() => {
                    setSelectedAttorney(caseData?.assignedAttorneyId || '');
                    setSelectedCaseManager(caseData?.assignedCaseManagerId || '');
                    setShowAssignModal(true);
                  }}
                >
                  <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  Reassign Case
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full justify-start text-gray-900"
                  onClick={() => setShowAppointmentModal(true)}
                >
                  <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  Schedule Appointment
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full justify-start text-gray-900"
                  onClick={() => setShowVideoConferenceModal(true)}
                >
                  <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  Schedule Video Conference
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full justify-start text-gray-900"
                  onClick={() => navigate(`/messages?clientId=${caseData.clientId}`)}
                >
                  <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                  </svg>
                  Send Message
                </Button>
                <Button variant="outline" className="w-full justify-start text-gray-900">
                  <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  Add Note
                </Button>
              </CardContent>
            </Card>

            {/* Assignment */}
            <Card>
              <CardHeader>
                <CardTitle>Assignment</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-xs font-medium text-gray-600">Attorney</label>
                  {assignedAttorney ? (
                    <div className="mt-2">
                      <p className="text-sm font-semibold text-gray-900">
                        {assignedAttorney.firstName} {assignedAttorney.lastName}
                      </p>
                      <p className="text-xs text-gray-600">{assignedAttorney.email}</p>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 mt-1 italic">Not assigned</p>
                  )}
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600">Case Manager</label>
                  {assignedCaseManager ? (
                    <div className="mt-2">
                      <p className="text-sm font-semibold text-gray-900">
                        {assignedCaseManager.firstName} {assignedCaseManager.lastName}
                      </p>
                      <p className="text-xs text-gray-600">{assignedCaseManager.email}</p>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 mt-1 italic">Not assigned</p>
                  )}
                </div>
                <Button 
                  variant="outline" 
                  className="w-full mt-4"
                  onClick={() => {
                    setSelectedAttorney(caseData?.assignedAttorneyId || '');
                    setSelectedCaseManager(caseData?.assignedCaseManagerId || '');
                    setShowAssignModal(true);
                  }}
                >
                  Change Assignment
                </Button>
              </CardContent>
            </Card>

            {/* Case Priority */}
            <Card>
              <CardHeader>
                <CardTitle>Case Priority</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-2">
                  <Badge variant="warning" className="text-sm">
                    🔥 Medium Priority
                  </Badge>
                </div>
                <p className="text-xs text-gray-600 mt-2">
                  Priority is automatically calculated based on case age, status, and activity.
                </p>
              </CardContent>
            </Card>

            {/* Video Conferences */}
            <VideoConferencesList
              conferences={videoConferences as any}
              loading={videoConferencesLoading}
              onRefetch={refetchVideoConferences}
              emptyMessage="No video conferences scheduled for this case"
              showScheduleButton={true}
              onScheduleClick={() => setShowVideoConferenceModal(true)}
            />
          </div>
        </div>
      </main>

      {/* Status Update Modal */}
      {showStatusModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Update Case Status</h3>
            <div className="space-y-3 mb-6">
              {['NEW', 'IN_PROGRESS', 'NEGOTIATION', 'LITIGATION', 'SETTLED', 'CLOSED'].map((status) => (
                <label key={status} className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
                  <input
                    type="radio"
                    name="status"
                    value={status}
                    checked={selectedStatus === status}
                    onChange={(e) => setSelectedStatus(e.target.value)}
                    className="w-4 h-4 text-aalaw-red"
                  />
                  <span className="text-sm font-medium text-gray-900">{status.replace('_', ' ')}</span>
                </label>
              ))}
            </div>
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => {
                  setShowStatusModal(false);
                  setSelectedStatus('');
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleStatusUpdate}
                disabled={!selectedStatus}
              >
                Update Status
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Assignment Modal */}
      {showAssignModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Assign Case</h3>
            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Attorney
                </label>
                <select
                  value={selectedAttorney}
                  onChange={(e) => setSelectedAttorney(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-aalaw-red focus:border-transparent"
                >
                  <option value="">Select Attorney</option>
                  {attorneys.map((attorney: any) => (
                    <option key={attorney.id} value={attorney.id}>
                      {attorney.firstName} {attorney.lastName}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Case Manager
                </label>
                <select
                  value={selectedCaseManager}
                  onChange={(e) => setSelectedCaseManager(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-aalaw-red focus:border-transparent"
                >
                  <option value="">Select Case Manager</option>
                  {caseManagers.map((manager: any) => (
                    <option key={manager.id} value={manager.id}>
                      {manager.firstName} {manager.lastName}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => {
                  setShowAssignModal(false);
                  setSelectedAttorney('');
                  setSelectedCaseManager('');
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleAssignment}
                disabled={!selectedAttorney && !selectedCaseManager}
              >
                Assign Case
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Create Task Modal */}
      {showCreateTaskModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 my-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Assign New Task</h3>
            
            {/* Template Selection Option */}
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-gray-700">
                  Use Template (Optional)
                </label>
                <button
                  onClick={() => navigate('/tasks/templates')}
                  className="text-xs text-blue-600 hover:text-blue-800"
                >
                  Manage Templates →
                </button>
              </div>
              <select
                value={selectedTemplateId}
                onChange={(e) => {
                  const templateId = e.target.value;
                  setSelectedTemplateId(templateId);
                  
                  if (templateId) {
                    // Find the template and populate fields
                    const template = templates.find((t: any) => t.id === templateId);
                    if (template) {
                      setTaskTitle(template.title || '');
                      setTaskDescription(template.description || '');
                      setTaskType(template.taskType);
                      setTaskPriority(template.priority);
                      
                      // Calculate due date based on defaultDueDays
                      const dueDate = new Date();
                      dueDate.setDate(dueDate.getDate() + (template.defaultDueDays || 7));
                      setTaskDueDate(dueDate.toISOString().split('T')[0]);
                    }
                  } else {
                    // Clear fields if no template selected
                    setTaskTitle('');
                    setTaskDescription('');
                    setTaskType('DOCUMENT_UPLOAD');
                    setTaskPriority('MEDIUM');
                    setTaskDueDate('');
                  }
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">-- Create Custom Task --</option>
                {templates.map((template: any) => (
                  <option key={template.id} value={template.id}>
                    {template.name} ({template.category})
                  </option>
                ))}
              </select>
              {selectedTemplateId && (
                <p className="text-xs text-gray-600 mt-2">
                  ✓ Template selected. You can still edit the fields below before assigning.
                </p>
              )}
            </div>

            <div className="space-y-4 mb-6 max-h-[60vh] overflow-y-auto">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Task Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={taskTitle}
                  onChange={(e) => setTaskTitle(e.target.value)}
                  placeholder="e.g., Upload Medical Records"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-aalaw-red focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={taskDescription}
                  onChange={(e) => setTaskDescription(e.target.value)}
                  placeholder="Provide clear instructions for the client..."
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-aalaw-red focus:border-transparent"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Task Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={taskType}
                    onChange={(e) => setTaskType(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-aalaw-red focus:border-transparent"
                  >
                    <option value="DOCUMENT_UPLOAD">Document Upload</option>
                    <option value="APPOINTMENT">Appointment</option>
                    <option value="FORM_COMPLETION">Form Completion</option>
                    <option value="COMMUNICATION">Communication</option>
                    <option value="REVIEW">Review</option>
                    <option value="COMPLIANCE">Compliance</option>
                    <option value="OTHER">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Priority <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={taskPriority}
                    onChange={(e) => setTaskPriority(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-aalaw-red focus:border-transparent"
                  >
                    <option value="LOW">Low</option>
                    <option value="MEDIUM">Medium</option>
                    <option value="HIGH">High</option>
                    <option value="URGENT">Urgent</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Due Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={taskDueDate}
                  onChange={(e) => setTaskDueDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-aalaw-red focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Staff Notes (Internal Only)
                </label>
                <textarea
                  value={taskStaffNotes}
                  onChange={(e) => setTaskStaffNotes(e.target.value)}
                  placeholder="Internal notes not visible to client..."
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-aalaw-red focus:border-transparent"
                />
              </div>
            </div>
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => {
                  setShowCreateTaskModal(false);
                  setTaskTitle('');
                  setTaskDescription('');
                  setTaskType('DOCUMENT_UPLOAD');
                  setTaskPriority('MEDIUM');
                  setTaskDueDate('');
                  setTaskStaffNotes('');
                  setSelectedTemplateId('');
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleCreateTask}
              >
                Assign Task
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Task Detail Modal - Using Shared Component */}
      <TaskDetailModal
        task={selectedTask}
        isOpen={showTaskDetailModal}
        onClose={() => {
          setShowTaskDetailModal(false);
          setSelectedTask(null);
        }}
        onTaskUpdated={refetchTasks}
      />

      {/* Appointment Modal */}
      {showAppointmentModal && (
        <AppointmentModal
          preSelectedCaseId={id}
          onClose={() => setShowAppointmentModal(false)}
        />
      )}

      {/* Schedule Video Conference Modal */}
      <ScheduleConferenceModal
        isOpen={showVideoConferenceModal}
        onClose={() => setShowVideoConferenceModal(false)}
        onCreated={() => {
          refetchVideoConferences();
          setShowVideoConferenceModal(false);
        }}
      />

      {/* File Viewer Modal */}
      <FileViewerModal
        visible={fileViewerVisible}
        files={fileViewerFiles}
        initialIndex={fileViewerIndex}
        onClose={() => setFileViewerVisible(false)}
      />
    </div>
  );
}
